import {
  Box,
  Divider,
  Drawer,
  Fab,
  Link,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { Icons } from 'utoniq-ui'

import { ItemType } from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsRight,
  BaseLayout,
  Head,
} from '@/components/layouts'
import { ItemSelectBox, ItemSelectBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { SearchForm } from './SearchForm'

export * from './fragments'

import type { Action, State } from '../viewModel'

type MusicSelectCellProps = {
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  items: State['items']
  onFetchMore: Action['onFetchMore']
  onRemove: Action['onRemove']
  onToggleActive: Action['onToggleActive']
  onToggleSelling: Action['onToggleSelling']
}

export const ConsoleItemsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onRemove,
  onSearch,
  onToggleActive,
  onToggleSelling,
  onFilter,
}) => {
  const { t } = useTranslation('consoleItems')
  const { routerNames, routerPages } = useRouter()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleItems} />

      <BaseLayout
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.user}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 5, py: 3, mt: 2 }}
        >
          <Typography variant="h3">{t('title')}</Typography>
          <Link href={routerPages.ConsoleItemOrders} variant="subtitle1">
            {t('purchaseHistory')}
          </Link>
        </Stack>
        <SearchForm onFilter={onFilter} onSearch={onSearch} />
        <MusicSelectCell
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          items={state.items}
          onFetchMore={onFetchMore}
          onRemove={onRemove}
          onToggleActive={onToggleActive}
          onToggleSelling={onToggleSelling}
        />
        <BaseCell />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const MusicSelectCell: Cell<MusicSelectCellProps> = ({
  items,
  loading,
  hasNextPage,
  onFetchMore,
  onRemove,
  onToggleActive,
  onToggleSelling,
}) => {
  const { t } = useTranslation('consoleItems')
  return (
    <CellFrame sx={{ px: 5, py: 2 }}>
      <ScrollFetcher
        nodes={items?.edges.map(({ node }) => (
          <ItemSelectBox
            key={node.id}
            item={node}
            onRemove={onRemove}
            onToggleActive={onToggleActive}
            onToggleSelling={onToggleSelling}
            showIcon
          />
        ))}
        loading={loading}
        componentProps={{ spacing: 5 }}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        emptyMessage={t('itemEmptyMessage')}
        loadingComponent={
          <Stack spacing={5} sx={{ py: 2 }}>
            {Array.from(Array(4).keys()).map(key => (
              <ItemSelectBoxSkeleton key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}

const BaseCell: Cell = () => {
  const [open, setOpen] = useState<boolean>(false)

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab size="medium" color="primary" onClick={toggleDrawer(true)}>
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
      <CreateItemActionSheet open={open} onClose={toggleDrawer(false)} />
    </Stack>
  )
}

type CreateItemActionSheetProps = {
  open: boolean
  onClose: () => void
}
export const CreateItemActionSheet: FC<CreateItemActionSheetProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('consoleItems')
  const { routerPages, generatePath } = useRouter()
  const items = useMemo(
    () => [
      {
        Icon: Icons.Audio,
        text: t('sound'),
        href: generatePath(routerPages.ConsoleItemCreate, {
          resourceType: ItemType.Audio,
        }),
      },
      {
        Icon: Icons.Video,
        text: t('video'),
        href: generatePath(routerPages.ConsoleItemCreate, {
          resourceType: ItemType.Video,
        }),
      },
      {
        Icon: Icons.Magazine,
        text: t('magazine'),
        href: generatePath(routerPages.ConsoleItemCreate, {
          resourceType: ItemType.Magazine,
        }),
      },
      {
        Icon: Icons.Wallpaper,
        text: t('wallpaper'),
        href: generatePath(routerPages.ConsoleItemCreate, {
          resourceType: ItemType.Wallpaper,
        }),
      },
      {
        Icon: Icons.Merch,
        text: t('merch'),
        href: generatePath(routerPages.ConsoleItemCreate, {
          resourceType: ItemType.Merch,
        }),
      },
      {
        Icon: Icons.Ticket,
        text: t('ticket'),
        href: generatePath(routerPages.ConsoleItemCreate, {
          resourceType: ItemType.Ticket,
        }),
      },
    ],
    [generatePath, routerPages.ConsoleItemCreate, t]
  )
  return (
    <Drawer open={open} onClose={onClose}>
      <Box sx={{ p: 5 }}>
        <Typography variant="h6" color="grey.600" align="center">
          {t('selectItemTypeTitle')}
        </Typography>
      </Box>
      <Divider />
      <List>
        {items.map(({ Icon, text, href }, index) => (
          <ListItemButton sx={{ height: 52 }} href={href} key={index}>
            <Icon fontSize="small" />
            <Typography variant="h6" sx={{ pl: 3 }}>
              {text}
            </Typography>
          </ListItemButton>
        ))}
        <Divider />
        <ListItemButton
          component={Box}
          sx={{ height: 52 }}
          alignItems="center"
          onClick={onClose}
        >
          <Typography
            variant="h6"
            align="center"
            sx={{ width: '100%', color: 'primary.main' }}
          >
            {t('cancel')}
          </Typography>
        </ListItemButton>
      </List>
    </Drawer>
  )
}
